// In src/ScenePanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListTree, X } from 'lucide-react';
import SceneOutliner from './SceneOutliner'; // We'll create this next
import PropertiesPanel from './PropertiesPanel'; // And this one
import { useTranslation } from 'react-i18next';

export default function ScenePanel({ viewer }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [groupedObjects, setGroupedObjects] = useState({});
    const [selectedObjectUUID, setSelectedObjectUUID] = useState(null);

    const selectedObject = React.useMemo(() =>
        Object.values(groupedObjects).flat().find(obj => obj.uuid === selectedObjectUUID),
        [groupedObjects, selectedObjectUUID]
    );

    const forceSceneUpdate = useCallback(() => {
        if (!viewer) return;
        const scene = viewer.scene;
        const grouped = {};

        if (scene.pointclouds?.length > 0) grouped.pointclouds = [...scene.pointclouds];
        if (scene.measurements?.length > 0) grouped.measurements = [...scene.measurements];
        if (scene.volumes?.length > 0) grouped.volumes = [...scene.volumes];
        if (scene.profiles?.length > 0) grouped.profiles = [...scene.profiles];
        if (scene.annotations?.children?.length > 0) grouped.annotations = [...scene.annotations.children];
        
        // Hiding camera section
        // if (scene.cameraP) {
        //     scene.cameraP.name = "Camera";
        //     grouped.other = [scene.cameraP];
        // }

        setGroupedObjects(grouped);
    }, [viewer]);


    useEffect(() => {
        if (!viewer || !viewer.scene) return;
        const scene = viewer.scene;

        // Keep other object types synced using Potree's built-in events
        scene.addEventListener("measurement_added", forceSceneUpdate);
        scene.addEventListener("measurement_removed", forceSceneUpdate);
        scene.addEventListener("volume_added", forceSceneUpdate);
        scene.addEventListener("volume_removed", forceSceneUpdate);
        scene.addEventListener("profile_added", forceSceneUpdate);
        scene.addEventListener("profile_removed", forceSceneUpdate);

        // Initial populate
        forceSceneUpdate();

        return () => {
            scene.removeEventListener("measurement_added", forceSceneUpdate);
            scene.removeEventListener("measurement_removed", forceSceneUpdate);
            scene.removeEventListener("volume_added", forceSceneUpdate);
            scene.removeEventListener("volume_removed", forceSceneUpdate);
            scene.removeEventListener("profile_added", forceSceneUpdate);
            scene.removeEventListener("profile_removed", forceSceneUpdate);
        };
    }, [viewer, forceSceneUpdate]);

    useEffect(() => {
        if (!viewer) return;

        const scene = viewer.scene;
        const annotationsRoot = scene.annotations;
        let lastCount = annotationsRoot.children.length;

        const interval = setInterval(() => {
            const currentCount = annotationsRoot.children.length;

            // Patch missing props on all annotations
            annotationsRoot.children.forEach((annotation) => {
                if (!annotation.viewer) {
                    annotation.viewer = viewer;

                    const camera = scene.getActiveCamera();
                    const view = scene.view;

                    if (!annotation.cameraPosition) {
                        annotation.cameraPosition = camera.position.clone();
                    }
                    if (!annotation.cameraTarget) {
                        annotation.cameraTarget = view.getPivot();
                    }
                }
            });

            // Update outliner if count changed
            if (currentCount !== lastCount) {
                lastCount = currentCount;
                forceSceneUpdate();
            }
        }, 500);

        return () => clearInterval(interval);
    }, [viewer, forceSceneUpdate]);



    const handleSelectObject = (object) => {
        setSelectedObjectUUID(object.uuid); // Store the stable ID
    };

    const handleDeleteObject = (object) => {
        if (selectedObjectUUID === object.uuid) {
            setSelectedObjectUUID(null); // Deselect if it's the current one
        }
        // Use the correct constructor names
        switch (object.constructor.name) {
            case 'Measure': viewer.scene.removeMeasurement(object); break;
            case 'BoxVolume': viewer.scene.removeVolume(object); break;
            case 'SphereVolume': viewer.scene.removeVolume(object); break;
            case 'Profile': viewer.scene.removeProfile(object); break;
            case 'Annotation': viewer.scene.removeAnnotation(object); break;
            default: break;
        }
    };

    return (
        <>
            <motion.button
                className="scene-toggle-button"
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
            >
                <ListTree size={24} />
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="scene-panel"
                        initial={{ y: "20%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "20%", opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                        <div className="scene-panel-header">
                            <h3>{t("scenePanel.header")}</h3>
                            <button onClick={() => setIsOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="scene-panel-content">
                            <SceneOutliner
                                groupedObjects={groupedObjects}
                                // Pass the UUID down for comparison
                                selectedObjectUUID={selectedObjectUUID}
                                onSelect={handleSelectObject}
                                onDelete={handleDeleteObject}
                                t={t}
                            />
                            <PropertiesPanel
                                selectedObject={selectedObject}
                                onForceUpdate={forceSceneUpdate}
                                viewer={viewer} // Pass viewer for interactive properties
                                t={t}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}